import type {
  BrokerQuoteModality,
  OperationDetail,
  OperationInput,
} from '@/shared/api/resources';
import {
  moneyInputError,
  moneyToInput,
  parseMoney,
  rateInputError,
  rateToBasisPoints,
  rateToInput,
} from '@/shared/lib/format';

import type { Sugestao } from '../sugestoesDaOperacao';

import type { PassoDoAssistente } from './cotacaoDaCorretora';

/**
 * O rascunho do assistente de nova cotacao (P-NCT): os campos dos passos do tomador, do
 * segurado e do risco, guardados como digitados ate a cotacao ser gravada ao fim do
 * passo do risco. Depois disso, os mesmos campos editam a cotacao em em_cotacao.
 *
 * Nada aqui calcula: o premio e o do servidor (IS x taxa, pela estimativa), o prazo e o
 * fim da vigencia sao digitados (a conversao entre eles e PRC-008).
 */
export interface RascunhoDaCotacao {
  policyholder_id: string;
  policyholder_label: string;
  policyholder_branch_id: string;
  /** A caixa da declaracao do termo do tipo cotacao, marcada nesta sessao. */
  declarou: boolean;
  insured_id: string;
  insured_label: string;
  insured_document: string;
  insured_address_id: string;
  insured_address_label: string;
  grupo: string;
  modality_id: string;
  insured_amount: string;
  applied_rate: string;
  coverage_start: string;
  coverage_end: string;
  term_days: string;
  complementary_modality_id: string;
  complementary_coverage_start: string;
  subject: string;
  /** Codigo do quadro de distribuicao (NCT-215), opcional. */
  distribution_code: string;
}

export const rascunhoVazio: RascunhoDaCotacao = {
  policyholder_id: '',
  policyholder_label: '',
  policyholder_branch_id: '',
  declarou: false,
  insured_id: '',
  insured_label: '',
  insured_document: '',
  insured_address_id: '',
  insured_address_label: '',
  grupo: '',
  modality_id: '',
  insured_amount: '',
  applied_rate: '',
  coverage_start: '',
  coverage_end: '',
  term_days: '',
  complementary_modality_id: '',
  complementary_coverage_start: '',
  subject: '',
  distribution_code: '',
};

/** O rascunho de uma cotacao ja gravada, para a edicao antes do envio. */
export function rascunhoDaOperacao(op: OperationDetail): RascunhoDaCotacao {
  return {
    policyholder_id: op.policyholder.id,
    policyholder_label: op.policyholder.label,
    policyholder_branch_id: op.policyholder_branch?.id ?? '',
    declarou: false,
    insured_id: op.insured?.id ?? '',
    insured_label: op.insured?.label ?? '',
    insured_document: op.insured?.document ?? '',
    insured_address_id: op.insured_address?.id ?? '',
    insured_address_label: op.insured_address?.label ?? '',
    grupo: op.modality_info?.group?.id ?? '',
    modality_id: op.modality.id,
    insured_amount: moneyToInput(op.insured_amount),
    applied_rate: op.applied_rate_bps ? rateToInput(op.applied_rate_bps) : '',
    coverage_start: op.coverage_start ?? '',
    coverage_end: op.coverage_end ?? '',
    term_days: op.term_days ? String(op.term_days) : '',
    complementary_modality_id: op.complementary_modality?.id ?? '',
    complementary_coverage_start: op.complementary_coverage_start ?? '',
    subject: op.subject,
    distribution_code: op.distribution_code ?? '',
  };
}

/**
 * Avisos do prazo da modalidade no passo do risco (NCT-115): "O prazo minimo para essa
 * modalidade e de N dias" e "O prazo maximo para essa modalidade e de N dias", como no
 * OnPoint, quando o prazo digitado fica fora da vigencia minima ou maxima da
 * modalidade. So avisam e nao travam o passo: a trava do prazo e SUB-004, pendente de
 * regra. O prazo vazio nao avisa, porque a conversao do fim da vigencia em prazo e
 * PRC-008.
 */
export function avisosDoPrazo(
  modalidade: Pick<BrokerQuoteModality, 'min_term_days' | 'max_term_days'> | undefined,
  prazoEmDias: string,
): string[] {
  const prazo = prazoDigitado(prazoEmDias);
  if (!modalidade || prazo === null) return [];
  const avisos: string[] = [];
  if (modalidade.min_term_days !== null && prazo < modalidade.min_term_days) {
    avisos.push(
      `O prazo minimo para essa modalidade e de ${modalidade.min_term_days} dias.`,
    );
  }
  if (modalidade.max_term_days !== null && prazo > modalidade.max_term_days) {
    avisos.push(
      `O prazo maximo para essa modalidade e de ${modalidade.max_term_days} dias.`,
    );
  }
  return avisos;
}

/** Prazo em dias como digitado, ou null quando vazio ou invalido. */
export function prazoDigitado(texto: string): number | null {
  const limpo = texto.trim();
  if (!/^\d+$/.test(limpo)) return null;
  const dias = Number(limpo);
  return Number.isSafeInteger(dias) ? dias : null;
}

/**
 * O que falta no passo para seguir adiante, na linguagem da tela. O servidor confere de
 * novo ao gravar e ao enviar.
 */
export function faltasDoPasso(
  passo: PassoDoAssistente,
  rascunho: RascunhoDaCotacao,
  contexto: { termoVigente: boolean; taxaEditavel: boolean },
): string[] {
  const faltas: string[] = [];
  if (passo === 'tomador') {
    if (!rascunho.policyholder_id) faltas.push('Escolha o tomador.');
    if (contexto.termoVigente && !rascunho.declarou) {
      faltas.push('Marque a declaracao para seguir.');
    }
  }
  if (passo === 'segurado') {
    if (!rascunho.insured_id) faltas.push('Escolha o segurado.');
    if (!rascunho.insured_address_id) faltas.push('Escolha o endereco do segurado.');
  }
  if (passo === 'risco') {
    if (!rascunho.modality_id) faltas.push('Escolha a modalidade.');
    const valor = parseMoney(rascunho.insured_amount);
    if (!valor || valor.amount <= 0) faltas.push('Informe a importancia segurada.');
    if (contexto.taxaEditavel && rateInputError(rascunho.applied_rate)) {
      faltas.push('Corrija a taxa.');
    }
    if (!rascunho.coverage_start) faltas.push('Informe o inicio da vigencia.');
    if (!rascunho.coverage_end) faltas.push('Informe o fim da vigencia.');
    if (
      rascunho.term_days.trim() !== '' &&
      prazoDigitado(rascunho.term_days) === null
    ) {
      faltas.push('O prazo e um numero inteiro de dias.');
    }
    if (rascunho.subject.trim() === '') faltas.push('Descreva o objeto da garantia.');
  }
  return faltas;
}

/** Mensagem do campo da importancia segurada. */
export function erroDaImportancia(texto: string): string | undefined {
  return moneyInputError(texto);
}

/**
 * O pedido de abertura ou de edicao com o que o rascunho tem. A taxa so vai quando a
 * empresa deixa a corretora digitar (`faixa`); na taxa sugerida, o servidor usa a da
 * faixa vigente e ignora a do pedido.
 */
export function entradaDaCotacao(
  rascunho: RascunhoDaCotacao,
  modalidade: BrokerQuoteModality | undefined,
  taxaEditavel: boolean,
): OperationInput {
  const valor = parseMoney(rascunho.insured_amount);
  const entrada: OperationInput = {
    policyholder_id: rascunho.policyholder_id,
    policyholder_branch_id: rascunho.policyholder_branch_id,
    insured_id: rascunho.insured_id,
    insured_address_id: rascunho.insured_address_id,
    modality_id: rascunho.modality_id,
    coverage_start: rascunho.coverage_start,
    coverage_end: rascunho.coverage_end,
    term_days: prazoDigitado(rascunho.term_days),
    complementary_modality_id: rascunho.complementary_modality_id,
    complementary_coverage_start: rascunho.complementary_modality_id
      ? rascunho.complementary_coverage_start
      : '',
    subject: rascunho.subject.trim(),
    distribution_code: rascunho.distribution_code.trim(),
  };
  if (modalidade) entrada.product_id = modalidade.product.id;
  if (valor) entrada.insured_amount = valor;
  if (taxaEditavel && rascunho.applied_rate.trim() !== '') {
    entrada.applied_rate_bps = rateToBasisPoints(rascunho.applied_rate);
  }
  return entrada;
}

/**
 * O pedido de abertura da cotacao nova: o do rascunho, como apolice, com a comissao
 * sugerida (`sugerirComissao`, a mesma regra da Nova operacao, OPEN_QUESTIONS 2.97). A
 * corretora ve e muda a comissao no passo Dados financeiros, com a cotacao gravada.
 *
 * Sem sugestao, a abertura e a de antes, sem `commission_bps`. A edicao usa
 * `entradaDaCotacao` e nunca leva a comissao: a gravada fica como esta.
 */
export function entradaDaAbertura(
  rascunho: RascunhoDaCotacao,
  modalidade: BrokerQuoteModality | undefined,
  taxaEditavel: boolean,
  comissao: Sugestao | undefined,
): OperationInput {
  const entrada: OperationInput = {
    ...entradaDaCotacao(rascunho, modalidade, taxaEditavel),
    proposal_type: 'apolice',
  };
  if (
    comissao &&
    comissao.valor !== '' &&
    rateInputError(comissao.valor) === undefined
  ) {
    entrada.commission_bps = rateToBasisPoints(comissao.valor);
  }
  return entrada;
}
